<template>
  <div>
    <b-card>
      <TitleTable titleTable="GLP-1 Treatments" iconTable="fas fa-syringe" />
      <b-row class="m-1 mb-2">
        <b-col cols="12" class="d-flex justify-content-end">
          <GoBack class="mr-2" />
          <b-button variant="primary" @click="openAdd">
            <i class="fas fa-plus mr-1"></i> Add treatment
          </b-button>
        </b-col>
      </b-row>
      <b-table
        :items="items"
        :fields="fields"
        hover
        bordered
        responsive
        primary-key="id"
        show-empty
        empty-text="No records"
        @row-clicked="toggleDetails">
        <template #cell(show)="row">
          <b-form-checkbox
            v-model="row.detailsShowing"
            plain
            class="vs-checkbox-con"
            @change="row.toggleDetails" />
        </template>
        <template #cell(description)="data">
          <span class="text-muted">{{ data.item.description || '—' }}</span>
        </template>
        <template #cell(doses_summary)="data">
          <span v-if="data.item.doses && data.item.doses.length">
            {{ formatDosesSummary(data.item.doses) }}
          </span>
          <span v-else class="text-muted">—</span>
        </template>
        <template #cell(active)="data">
          <b-badge :variant="data.item.active ? 'success' : 'secondary'">
            {{ data.item.active ? 'Active' : 'Inactive' }}
          </b-badge>
        </template>
        <template #cell(actions)="data">
          <b-button size="sm" variant="outline-primary" class="mr-1" @click="openEdit(data.item)">
            <i class="fas fa-pen"></i>
          </b-button>
          <b-button size="sm" variant="outline-danger" @click="remove(data.item)">
            <i class="fas fa-trash-alt"></i>
          </b-button>
        </template>
        <template #row-details="row">
          <b-card>
            <strong class="d-block mb-2">Doses — {{ row.item.name }}</strong>
            <b-table
              v-if="sortedDosesFor(row.item).length"
              :items="sortedDosesFor(row.item)"
              :fields="doseDetailFields"
              small
              bordered
              responsive
              class="mb-2">
              <template #cell(qty_per_injection)="data">
                {{ formatQty(data.value) }}
              </template>
              <template #cell(inv_item)="data">
                {{ invItemLabel(data.item) || '—' }}
              </template>
              <template #cell(active)="data">
                <b-badge :variant="data.value ? 'success' : 'secondary'">
                  {{ data.value ? 'Active' : 'Inactive' }}
                </b-badge>
              </template>
            </b-table>
            <span v-else class="text-muted">No doses configured</span>
            <b-button size="sm" variant="outline-secondary" @click="row.toggleDetails">
              Hide details
            </b-button>
          </b-card>
        </template>
      </b-table>
    </b-card>

    <TreatmentFormModal
      :visible.sync="modalVisible"
      :edit-id="editId"
      :form="form"
      :item-options="itemOptions"
      :removed-dose-ids="removedDoseIds"
      :loading="saving"
      @save="onModalSave"
      @close="onModalClose" />

    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import { BCard, BRow, BCol, BTable, BButton, BBadge, BFormCheckbox } from 'bootstrap-vue'
import TitleTable from '@/components/TitleTable.vue'
import GoBack from '@/components/ButtonBack.vue'
import ToastNotification from '@/components/ToastNotification'
import TreatmentFormModal, { newDoseRow, mapDoseFromApi } from './TreatmentFormModal.vue'
import wlTreatmentsApi from '@/core/services/api/weightloss-inventory/treatments'
import wlDosesApi from '@/core/services/api/weightloss-inventory/doses'
import invItemsApi from '@/core/services/api/weightloss-inventory/items'
import { unwrapData } from '@/core/services/api/weightloss-inventory/helpers'
import Swal from 'sweetalert2'

export default {
  name: 'TableWlTreatments',
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BButton,
    BBadge,
    BFormCheckbox,
    TitleTable,
    GoBack,
    ToastNotification,
    TreatmentFormModal,
  },
  data() {
    return {
      items: [],
      itemOptions: [],
      removedDoseIds: [],
      fields: [
        'show',
        { key: 'id', label: 'ID' },
        { key: 'name', label: 'Name' },
        { key: 'description', label: 'Description' },
        { key: 'doses_summary', label: 'Doses' },
        { key: 'active', label: 'Status' },
        { key: 'actions', label: '' },
      ],
      doseDetailFields: [
        { key: 'sort_order', label: '#', thClass: 'text-center', tdClass: 'text-center', thStyle: { width: '56px' } },
        { key: 'dose_label', label: 'Label' },
        { key: 'qty_per_injection', label: 'Qty / injection', thClass: 'text-center', tdClass: 'text-center', thStyle: { width: '120px' } },
        { key: 'inv_item', label: 'Inventory item' },
        { key: 'active', label: 'Active', thClass: 'text-center', tdClass: 'text-center', thStyle: { width: '80px' } },
      ],
      modalVisible: false,
      saving: false,
      editId: null,
      form: {
        name: '',
        description: '',
        active: true,
        doses: [],
      },
    }
  },
  mounted() {
    this.loadItemOptions()
    this.load()
  },
  methods: {
    toggleDetails(item) {
      if (!item) return
      this.$set(item, '_showDetails', !item._showDetails)
    },
    formatDosesSummary(doses) {
      const labels = doses
        .slice()
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map(d => d.dose_label)
        .filter(Boolean)
      if (!labels.length) return `${doses.length} doses`
      if (labels.length <= 3) return labels.join(', ')
      return `${labels.slice(0, 3).join(', ')} (+${labels.length - 3})`
    },
    sortedDosesFor(item) {
      return (item.doses || [])
        .slice()
        .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0))
    },
    invItemLabel(dose) {
      if (dose.inv_item?.name) {
        const unit = dose.inv_item.unit ? ` (${dose.inv_item.unit})` : ''
        return `${dose.inv_item.name}${unit}`
      }
      if (!dose.inv_item_id) return null
      const opt = this.itemOptions.find(o => o.id === dose.inv_item_id)
      return opt ? opt.label : `#${dose.inv_item_id}`
    },
    formatQty(value) {
      if (value == null || value === '') return '—'
      const n = Number(value)
      return Number.isFinite(n) ? n : value
    },
    async loadItemOptions() {
      try {
        const { items } = await invItemsApi.listPaginated(100, {})
        this.itemOptions = items.map(i => ({ id: i.id, label: `${i.name} (${i.unit || ''})` }))
      } catch {
        this.itemOptions = []
      }
    },
    async load() {
      try {
        this.items = await wlTreatmentsApi.list()
      } catch (e) {
        this.$refs.toast.danger(e.message)
      }
    },
    resetForm() {
      this.removedDoseIds = []
      this.form = {
        name: '',
        description: '',
        active: true,
        doses: [newDoseRow(1)],
      }
    },
    openAdd() {
      this.editId = null
      this.resetForm()
      this.modalVisible = true
    },
    openEdit(item) {
      this.editId = item.id
      this.removedDoseIds = []
      const doses = (item.doses || []).map((d, i) => mapDoseFromApi(d, i))
      this.form = {
        name: item.name,
        description: item.description || '',
        active: !!item.active,
        doses: doses.length ? doses : [newDoseRow(1)],
      }
      this.modalVisible = true
    },
    onModalClose() {
      this.removedDoseIds = []
    },
    onModalSave({ form, removedDoseIds }) {
      this.form = form
      this.removedDoseIds = removedDoseIds
      this.save()
    },
    buildTreatmentPayload() {
      return {
        name: this.form.name,
        description: this.form.description || null,
        active: this.form.active,
      }
    },
    buildDosePayload(dose, treatmentId) {
      return {
        weightloss_treatment_id: treatmentId,
        dose_label: dose.dose_label,
        qty_per_injection: Number(dose.qty_per_injection),
        sort_order: Number(dose.sort_order) || 1,
        inv_item_id: dose.inv_item_id || null,
        active: !!dose.active,
      }
    },
    async syncDoses(treatmentId) {
      for (const doseId of this.removedDoseIds) {
        await wlDosesApi.remove(doseId)
      }
      for (const dose of this.form.doses) {
        if (!dose.dose_label || !String(dose.dose_label).trim()) continue
        const payload = this.buildDosePayload(dose, treatmentId)
        if (dose.id) {
          await wlDosesApi.update(dose.id, payload)
        } else {
          await wlDosesApi.create(payload)
        }
      }
    },
    async save() {
      if (!this.form.name || !String(this.form.name).trim()) {
        this.$refs.toast.danger('Treatment name is required')
        return
      }
      const validDoses = this.form.doses.filter(d => d.dose_label && String(d.dose_label).trim())
      if (!validDoses.length) {
        this.$refs.toast.danger('Add at least one dose with a label')
        return
      }

      this.saving = true
      try {
        let treatmentId = this.editId
        const treatmentPayload = this.buildTreatmentPayload()

        if (this.editId) {
          await wlTreatmentsApi.update(this.editId, treatmentPayload)
        } else {
          const res = await wlTreatmentsApi.create(treatmentPayload)
          const created = unwrapData(res)
          treatmentId = created?.id
        }

        if (!treatmentId) {
          throw new Error('Could not get treatment ID to save doses')
        }

        await this.syncDoses(treatmentId)
        this.$refs.toast.success(
          this.editId ? 'Treatment and doses updated' : 'Treatment and doses created'
        )
        this.modalVisible = false
        this.load()
      } catch (e) {
        this.$refs.toast.danger(e.message)
      } finally {
        this.saving = false
      }
    },
    remove(item) {
      Swal.fire({
        title: 'Delete treatment?',
        text: 'The treatment will be deleted; associated doses may become orphaned depending on the backend.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
      }).then(async r => {
        if (r.value) {
          try {
            await wlTreatmentsApi.remove(item.id)
            this.$refs.toast.success('Deleted')
            this.load()
          } catch (e) {
            this.$refs.toast.danger(e.message)
          }
        }
      })
    },
  },
}
</script>
